import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  CreditCard, 
  AlertTriangle, 
  RefreshCw,
  Crown
} from 'lucide-react';
import { renewSubscription, usePlanById } from '../../services/subscriptionService';
import { useToastContext } from '../../contexts/ToastContext';
import { planIsFree } from '../../utils/subscriptionPlanPricing';

const RenewalModal = ({ 
  isOpen, 
  onClose, 
  subscriptionDetails, 
  onSuccess 
}) => {
  const [renewing, setRenewing] = useState(false);
  const navigate = useNavigate();
  const { success, error: showError } = useToastContext();
  
  // Use the general usePlanById hook to get plan details
  const { plan: planDetails, isLoading: loadingPlans, error: plansError } = usePlanById(
    subscriptionDetails?.subscription_plan_id
  );

  // Handle plans loading error
  if (plansError && isOpen) {
    showError('Failed to load plan details');
  }

  if (!isOpen || !subscriptionDetails) return null;

  const isFreePlan = Boolean(
    subscriptionDetails.is_free_tier || planIsFree(planDetails)
  );

  const handleChoosePaidPlan = () => {
    onClose();
    navigate('/vendor/subscription');
  };

  const handleRenewal = async () => {
    if (isFreePlan) {
      handleChoosePaidPlan();
      return;
    }
    if (!planDetails) {
      showError('Plan details not loaded. Please try again.');
      return;
    }

    try {
      setRenewing(true);
      
      const renewalData = {
        payment_method: 'stripe', // Default payment method
        payment_reference: `renew_${Date.now()}`,
        payment_details: {
          method: 'stripe',
          timestamp: new Date().toISOString(),
          amount: planDetails.price,
          currency: planDetails.currency || 'USD',
          type: 'renewal',
          plan_name: planDetails.name,
          plan_id: planDetails.id
        }
      };

      const response = await renewSubscription(renewalData);
      
      if (response.success) {
        success(`Subscription renewed successfully! Your ${planDetails.name} plan has been extended by 30 days.`);
        
        // Call success callback to refresh data
        onSuccess?.();
        
        // Close modal
        onClose();
      }
    } catch (error) {
      showError(error.message || 'Renewal failed. Please try again.');
    } finally {
      setRenewing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CreditCard className="h-5 w-5 mr-2 text-primary-600" />
              {isFreePlan ? 'Upgrade Your Plan' : 'Renew Subscription'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Current Plan</h4>
              <p className="text-lg font-semibold text-primary-600">{subscriptionDetails.plan_name}</p>
              <p className="text-sm text-gray-600 mt-1">
                Expires in {subscriptionDetails.days_remaining} day{subscriptionDetails.days_remaining !== 1 ? 's' : ''}
              </p>
              {loadingPlans ? (
                <div className="flex items-center mt-2">
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  <span className="text-sm text-gray-500">Loading plan details...</span>
                </div>
              ) : planDetails ? (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Price:</span> ${planDetails.price}
                    <span className="text-gray-500">/{planDetails.billing_period || 'month'}</span>
                  </p>
                  {planDetails.description && (
                    <p className="text-xs text-gray-500 mt-1">{planDetails.description}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-red-600 mt-2">Failed to load plan details</p>
              )}
            </div>

            {isFreePlan ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-900 mb-2">Choose a paid plan to continue</h4>
                <div className="space-y-2 text-sm text-amber-800">
                  <p>• Your free plan cannot be renewed for another term</p>
                  <p>• Select a paid plan to keep your listings, bookings, and vendor tools active</p>
                  <p>• Paid plans include higher limits, analytics, branding, and premium features</p>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Renewal Details</h4>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>• Your subscription will be extended by 30 days</p>
                  <p>• Payment will be processed immediately</p>
                  {planDetails && (
                    <p>• Amount to be charged: <span className="font-medium">${planDetails.price} {planDetails.currency || 'USD'}</span></p>
                  )}
                  <p>• You'll receive a confirmation email</p>
                  <p>• Service will continue without interruption</p>
                </div>
              </div>
            )}

            {!isFreePlan && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Important</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Renewing will extend your current plan. If you want to change plans, 
                      please visit the subscription page instead.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className={`flex gap-3 pt-4 ${isFreePlan ? 'flex-col' : 'flex-row'}`}>
              <button
                onClick={onClose}
                disabled={renewing}
                className={`${isFreePlan ? 'w-full' : 'flex-1'} px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Cancel
              </button>
              {isFreePlan ? (
                <button
                  onClick={handleChoosePaidPlan}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <Crown className="h-4 w-4 shrink-0" />
                  Choose a paid plan
                </button>
              ) : (
                <button
                  onClick={handleRenewal}
                  disabled={renewing || loadingPlans || !planDetails}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {renewing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : loadingPlans ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Crown className="h-4 w-4 mr-2" />
                      {planDetails ? `Renew Now - $${planDetails.price}` : 'Renew Now'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalModal;
